import { NgxPermissionsService } from 'ngx-permissions';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, filter, map, take, withLatestFrom } from 'rxjs/operators';
import { BreadcrumbService } from 'xng-breadcrumb';
import { ActivatedRouteSnapshot } from '@angular/router';
import { LazyFacadeInterface, SharedFacadeInterface } from '@ellf/app-common';
import { RouterService } from '@ellf/app-routing';
import { BaseDbDto } from '@ellf/common-dto';
import { DetailGuard } from './detail.guard';

export class LazyDetailGuard<T extends BaseDbDto> extends DetailGuard<T> {
  constructor(
    protected sharedFacade: SharedFacadeInterface<T>,
    protected facade: LazyFacadeInterface<T>,
    breadcrumb: BreadcrumbService,
    routerService: RouterService,
    permissionService?: NgxPermissionsService,
  ) {
    super(sharedFacade, facade, breadcrumb, routerService, permissionService);
  }

  hasEntity(
    id: string,
    disableForm: boolean,
    disableBreadcrumb: boolean,
    route: ActivatedRouteSnapshot,
  ): Observable<boolean> {
    return this.facade.getLoadedEntity$.pipe(
      withLatestFrom(this.permissionService?.permissions$ || of(undefined)),
      map(([item, permissions]) => {
        const createPermissions = this.hasCreatePermission(permissions);
        const updatePermissions = this.hasUpdatePermission(permissions);
        const isNew = id === '0';
        const load = !isNew && (item === undefined || item?.id !== id);
        if (load) {
          this.sharedFacade.loadSingle(id);
        } else {
          item = !isNew ? item : undefined;
          this.facade.formInitialize(
            item,
            disableForm || (isNew ? !createPermissions : !updatePermissions),
            this.getOptions(route),
            permissions ? Object.keys(permissions) : [],
          );
          this.setBreadcrumb(item, disableBreadcrumb);
        }
        return !load;
      }),
      filter((res) => !!res),
      take(1),
      catchError((err) => {
        return EMPTY;
      }),
    );
  }
}
